import { EventEmitter } from "events";
import { isPlatform } from "@shared/utils/get-platform";

/**
 * DesktopBridge - Event-driven messaging service between Next.js and Electron
 *
 * Features:
 * - Pure event-driven (no polling)
 * - Query-Response pattern for request/response communication
 * - Message queue for reliability (messages queued until desktop ready)
 *
 * Communication Flow:
 * 1. Next.js initializes bridge, sets up listeners
 * 2. Bridge sends query:status (queued if desktop not ready)
 * 3. Desktop fires desktop:ready OR responds to query
 * 4. Bridge flushes queue, emits "ready" event
 */

// Extend Window interface for desktop bridge globals
declare global {
  interface Window {
    __DESKTOP_READY__?: boolean;
  }
}

// Message types for bridge communication
export type BridgeMessageType =
  // Events (fire-and-forget from desktop)
  | "desktop:ready"
  | "desktop:disconnecting"
  | "desktop:agent-status-changed"
  // Queries (expect response)
  | "query:status"
  | "query:agent-health"
  // Responses
  | "response:status"
  | "response:agent-health";

/**
 * Bridge message structure
 */
export interface BridgeMessage<T = unknown> {
  id: string;
  type: BridgeMessageType;
  payload?: T;
  timestamp: number;
}

/**
 * Status response payload
 */
export interface StatusPayload {
  ready: boolean;
  version?: string;
  queryId?: string;
}

/**
 * Health response payload
 */
export interface HealthPayload {
  healthy: boolean;
  queryId?: string;
}

/**
 * Internal queued message structure
 */
interface QueuedMessage {
  message: BridgeMessage;
  resolve?: (response: BridgeMessage) => void;
  reject?: (error: Error) => void;
  timeout?: NodeJS.Timeout;
}

/**
 * DesktopBridgeService - Singleton service for desktop communication
 */
class DesktopBridgeService extends EventEmitter {
  private initialized = false;
  private desktopReady = false;
  private messageQueue: QueuedMessage[] = [];
  private pendingQueries: Map<string, QueuedMessage> = new Map();

  /**
   * Initialize the bridge - sets up event listeners
   * Automatically queries desktop status
   * Safe to call multiple times (idempotent)
   * Only initializes when running inside Electron desktop app
   */
  initialize(): void {
    if (this.initialized || typeof window === "undefined") return;

    // Platform guard: Skip initialization if not running on desktop
    // This is a defense-in-depth measure - the provider should already guard this
    if (!isPlatform("desktop")) {
      return;
    }

    this.initialized = true;

    // Set up listeners for messages from desktop
    window.addEventListener("message", this.handleWindowMessage);
    window.addEventListener("desktop-message", this.handleCustomEvent);

    // Query desktop status - will be queued if desktop not ready yet
    this.query<StatusPayload>("query:status")
      .then((response) => {
        if (response.payload?.ready) {
          this.desktopReady = true;
          this.emit("ready", response.payload);
        }
      })
      .catch(() => {
        // Desktop not available yet - that's okay
        // We'll receive "desktop:ready" event when it becomes available
        // eslint-disable-next-line no-console
        console.log(
          "[DesktopBridge] Desktop not ready yet, waiting for ready event..."
        );
      });
  }

  /**
   * Send a fire-and-forget event to desktop
   * Messages are queued if desktop is not ready
   */
  send(type: BridgeMessageType, payload?: unknown): void {
    if (typeof window === "undefined") return;

    const message = this.createMessage(type, payload);

    if (this.desktopReady) {
      this.dispatchToDesktop(message);
    } else {
      this.enqueue({ message });
    }
  }

  /**
   * Send a query and wait for response
   * Queries are queued if desktop is not ready
   *
   * @param type - Query message type
   * @param payload - Optional payload
   * @param timeout - Timeout in ms (default 5000)
   * @returns Promise that resolves with response message
   */
  query<T = unknown>(
    type: BridgeMessageType,
    payload?: unknown,
    timeout = 5000
  ): Promise<BridgeMessage<T>> {
    return new Promise((resolve, reject) => {
      if (typeof window === "undefined") {
        reject(new Error("Cannot query in SSR environment"));
        return;
      }

      const message = this.createMessage(type, payload);

      const timeoutId = setTimeout(() => {
        this.pendingQueries.delete(message.id);
        this.removeFromQueue(message.id);
        reject(new Error(`Query timeout: ${type}`));
      }, timeout);

      const queuedMessage: QueuedMessage = {
        message,
        resolve: resolve as (response: BridgeMessage) => void,
        reject,
        timeout: timeoutId,
      };

      this.pendingQueries.set(message.id, queuedMessage);

      if (this.desktopReady) {
        this.dispatchToDesktop(message);
      } else {
        this.enqueue(queuedMessage);
      }
    });
  }

  /**
   * Subscribe to a specific message type
   *
   * @param type - Message type to subscribe to
   * @param handler - Handler function
   * @returns Unsubscribe function
   */
  subscribe<T = unknown>(
    type: BridgeMessageType | "ready",
    handler: (payload: T, message?: BridgeMessage<T>) => void
  ): () => void {
    const wrappedHandler = (messageOrPayload: BridgeMessage<T> | T) => {
      // Handle both direct payload (for "ready") and message objects
      if (
        typeof messageOrPayload === "object" &&
        messageOrPayload !== null &&
        "type" in messageOrPayload
      ) {
        handler(
          (messageOrPayload as BridgeMessage<T>).payload as T,
          messageOrPayload as BridgeMessage<T>
        );
      } else {
        handler(messageOrPayload as T);
      }
    };
    this.on(type, wrappedHandler);
    return () => this.off(type, wrappedHandler);
  }

  /**
   * Check if desktop is ready
   */
  isDesktopReady(): boolean {
    return this.desktopReady;
  }

  /**
   * Check if bridge is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get queue length (for debugging)
   */
  getQueueLength(): number {
    return this.messageQueue.length;
  }

  /**
   * Destroy and cleanup all resources
   */
  destroy(): void {
    if (typeof window === "undefined") return;

    window.removeEventListener("message", this.handleWindowMessage);
    window.removeEventListener("desktop-message", this.handleCustomEvent);

    // Clear pending queries with rejection
    this.pendingQueries.forEach((q) => {
      if (q.timeout) clearTimeout(q.timeout);
      q.reject?.(new Error("Bridge destroyed"));
    });
    this.pendingQueries.clear();
    this.messageQueue = [];
    this.removeAllListeners();
    this.initialized = false;
    this.desktopReady = false;
  }

  // ===== Private Methods =====

  /**
   * Create a new message with unique ID
   */
  // eslint-disable-next-line class-methods-use-this
  private createMessage(
    type: BridgeMessageType,
    payload?: unknown
  ): BridgeMessage {
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      type,
      payload,
      timestamp: Date.now(),
    };
  }

  /**
   * Add message to queue
   */
  private enqueue(item: QueuedMessage): void {
    this.messageQueue.push(item);
  }

  /**
   * Remove message from queue by ID
   */
  private removeFromQueue(messageId: string): void {
    this.messageQueue = this.messageQueue.filter(
      (q) => q.message.id !== messageId
    );
  }

  /**
   * Flush all queued messages to desktop
   */
  private flushQueue(): void {
    const queue = [...this.messageQueue];
    this.messageQueue = [];

    queue.forEach((item) => {
      this.dispatchToDesktop(item.message);
    });
  }

  /**
   * Dispatch message to desktop via CustomEvent
   */
  // eslint-disable-next-line class-methods-use-this
  private dispatchToDesktop(message: BridgeMessage): void {
    if (typeof window === "undefined") return;

    window.dispatchEvent(new CustomEvent("web-message", { detail: message }));
  }

  /**
   * Handle window.postMessage events (for iframe communication)
   */
  private handleWindowMessage = (event: MessageEvent): void => {
    // Only process messages from desktop bridge
    if (event.data?.source !== "desktop-bridge") return;
    this.processIncomingMessage(event.data as BridgeMessage);
  };

  /**
   * Handle CustomEvent messages (for same-window communication)
   */
  private handleCustomEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<BridgeMessage>;
    if (customEvent.detail) {
      this.processIncomingMessage(customEvent.detail);
    }
  };

  /**
   * Process incoming message from desktop
   */
  private processIncomingMessage(message: BridgeMessage): void {
    // Handle desktop ready event
    if (message.type === "desktop:ready") {
      const wasReady = this.desktopReady;
      this.desktopReady = true;

      // Flush queued messages now that desktop is ready
      this.flushQueue();

      // Only emit if this is the first ready event
      if (!wasReady) {
        this.emit("desktop:ready", message);
        this.emit("ready", message.payload);
      }
      return;
    }

    // Handle response to pending query
    if (message.type.startsWith("response:")) {
      const payload = message.payload as { queryId?: string };
      const queryId = payload?.queryId;

      if (queryId && this.pendingQueries.has(queryId)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const pending = this.pendingQueries.get(queryId)!;
        if (pending.timeout) clearTimeout(pending.timeout);
        this.pendingQueries.delete(queryId);
        pending.resolve?.(message);
        return;
      }
    }

    // Emit for general subscribers
    this.emit(message.type, message);
  }
}

/**
 * Singleton instance of DesktopBridgeService
 * Note: Instance is created but NOT initialized on import
 * Call desktopBridge.initialize() explicitly to set up listeners
 */
export const desktopBridge = new DesktopBridgeService();
